import { useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "./ErrorBoundary";
import { BrandLogo, EcosystemMark } from "./Brand";
import { BellIcon, ChevronDown, ChevronLeft, ChevronRight, LogOutIcon, PanelLeftClose, PanelLeftOpen, PlusIcon, UserIcon } from "./Icons";
import { api } from "../api";
import { useTxFlow } from "../txflow";
import { useEntities } from "../entity";
import { useCompany } from "./CompanyLogo";
import { useFavorites } from "../favorites";
import { usePeriod } from "../period";
import { THEME_LABEL, useTheme, type ThemePref } from "../theme";

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  end?: boolean;
  children?: { to: string; label: string; end?: boolean }[];
}

const COLLAPSE_KEY = "mm.sidebarCollapsed";
const readCollapsed = () => { try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; } };

function MenuIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /></svg>;
}

function useOutside(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) close(); };
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", h); document.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", k); };
  }, [open, close]);
  return ref;
}

/** Telas estreitas (celular e tablet em pé): o menu lateral vira gaveta. */
const NARROW = "(max-width: 900px)";
function useNarrow() {
  const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(NARROW).matches;
  const [n, setN] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const m = window.matchMedia(NARROW), h = () => setN(m.matches);
    h(); m.addEventListener("change", h); return () => m.removeEventListener("change", h);
  }, []);
  return n;
}

const monthLabel = (d: Date) => {
  const s = d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }).replace(" de ", " de ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};
/** Avatar do topo: mostra o logotipo da empresa (PJ) ou a foto de perfil (PF) cadastrados em Configurações; sem imagem, as iniciais. */
function PjAvatar({ name, realm }: { name: string; realm: "pf" | "pj" }) {
  const { current } = useEntities();
  const { logoUrl } = useCompany(realm, current?.id);
  return logoUrl ? <span className={`avatar logo${realm === "pf" ? " photo" : ""}`}><img src={logoUrl} alt={realm === "pf" ? "Foto de perfil" : "Logotipo da empresa"} /></span> : <span className="avatar">{initials(name)}</span>;
}
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("") || "?";

export interface ShellProps {
  nav: NavItem[];
  realm: "pf" | "pj" | "admin";
  contextLabel: string;     // contexto: Pessoa Física / Pessoa Jurídica / Administração
  entity?: ReactNode;       // entidade em uso (PJ: seletor de empresa)
  displayName: string;
  email: string;
  settingsTo?: string;
  onLogout: () => void;
  children: ReactNode;
}

/** Estrutura aprovada: menu lateral azul-marinho recolhível, cabeçalho (contexto, entidade, período, +Novo, notificações, perfil). Sem busca global. */
export function Shell(p: ShellProps) {
  const [collapsedPref, setCollapsed] = useState(readCollapsed);
  const narrow = useNarrow();
  const collapsed = collapsedPref && !narrow;           // na gaveta o menu sempre aparece completo
  const [drawer, setDrawer] = useState(false);
  const toggleCollapsed = () => setCollapsed((c) => { try { localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1"); } catch { /* ok */ } return !c; });
  const client = p.realm !== "admin";
  const { period, shift } = usePeriod();
  const loc = useLocation();
  useEffect(() => { setDrawer(false); }, [loc.pathname, loc.search]);
  useEffect(() => { if (!narrow) setDrawer(false); }, [narrow]);
  useEffect(() => {
    if (!drawer) return;
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") setDrawer(false); };
    document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k);
  }, [drawer]);
  const favs = useFavorites(p.realm);
  const flat = p.nav.flatMap((n) => [{ to: n.to, label: n.label, icon: n.icon }, ...(n.children ?? []).map((c) => ({ to: c.to, label: c.label, icon: n.icon }))]);
  const favItems = favs.list.map((to) => flat.find((f) => f.to === to)).filter((x): x is (typeof flat)[number] => !!x);
  const star = (to: string, label: string) => client && !collapsed ? (
    <button type="button" className={`fav-star nav-star${favs.has(to) ? " on" : ""}`} aria-pressed={favs.has(to)} aria-label={favs.has(to) ? `Remover ${label} dos favoritos` : `Favoritar ${label}`} title={favs.has(to) ? "Remover dos favoritos" : "Favoritar (atalho na barra superior)"} onClick={() => favs.toggle(to)}>{favs.has(to) ? "★" : "☆"}</button>) : null;

  return (
    <div className="app">
      <div className={`nav-backdrop${drawer ? " open" : ""}`} onClick={() => setDrawer(false)} aria-hidden="true" data-nav-backdrop />
      <aside className={`sidebar${collapsed ? " collapsed" : ""}${drawer ? " open" : ""}`} aria-label="Menu lateral" id="menu-lateral">
        <button className="sb-icon-btn sb-collapse" onClick={toggleCollapsed} aria-label={collapsed ? "Expandir menu" : "Recolher menu"} title={collapsed ? "Expandir menu" : "Recolher menu"}>
          {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </button>
        <div className="sb-logo"><BrandLogo collapsed={collapsed} /></div>
        <nav className="sb-nav" aria-label="Principal">
          {p.nav.map((n) => {
            const Icon = n.icon;
            return (
              <div key={n.to}>
                <div className="nav-row">
                <NavLink to={n.to} end={n.end} title={n.label} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
                  <Icon size={20} />
                  {!collapsed && <span>{n.label}</span>}
                </NavLink>
                {star(n.to, n.label)}
                </div>
                {!collapsed && n.children && (loc.pathname.startsWith(n.to) || n.children.some((c) => loc.pathname.startsWith(c.to))) && (
                  <div className="nav-sub">{n.children.map((c) => (
                    <div className="nav-row" key={c.to}><NavLink to={c.to} end={c.end} className={({ isActive }) => `nav-subitem${isActive ? " active" : ""}`}>{c.label}</NavLink>{star(c.to, c.label)}</div>))}</div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="sb-foot">
          {!collapsed && <EcosystemMark />}
          <div className="build" title={`Versão ${__APP_VERSION__} · build ${__BUILD_ID__}`}>{collapsed ? "f2" : `v${__APP_VERSION__} · ${__BUILD_ID__}`}</div>
        </div>
      </aside>
      <div className="content">
        <header className="topbar" aria-label="Cabeçalho">
          <button type="button" className="top-icon nav-toggle" onClick={() => setDrawer((d) => !d)} aria-label={drawer ? "Fechar menu" : "Abrir menu"} aria-expanded={drawer} aria-controls="menu-lateral" data-nav-toggle><MenuIcon /></button>
          <span className="chip" title="Contexto da sessão atual (não substitui a autenticação do outro ambiente)"><UserIcon size={18} />{p.contextLabel}</span>
          {p.entity}
          {client && (
            <div className="period" role="group" aria-label="Período">
              <button className="top-icon" onClick={() => shift(-1)} aria-label="Mês anterior"><ChevronLeft /></button>
              <span className="label tabular">{monthLabel(period)}</span>
              <button className="top-icon" onClick={() => shift(1)} aria-label="Próximo mês"><ChevronRight /></button>
            </div>
          )}
          {client && favItems.length > 0 && (
            <nav className="fav-bar" aria-label="Favoritos">{favItems.map((f) => { const I = f.icon; return <NavLink key={f.to} to={f.to} title={f.label} aria-label={f.label} className={({ isActive }) => `top-icon fav-link${isActive ? " active" : ""}`}><I size={20} /></NavLink>; })}</nav>)}
          <span className="spacer" />
          {client && !CADASTRO_ROUTE.test(loc.pathname) && <NewMenu />}
          {p.realm !== "admin" && <Notifications realm={p.realm} />}
          <ProfileMenu {...p} />
        </header>
        <main className="main"><ErrorBoundary key={loc.pathname}>{p.children}</ErrorBoundary></main>
      </div>
    </div>
  );
}

/** Telas de cadastro (contas, categorias, beneficiários, centros, bens, configurações): sem "+ Novo" para não confundir cadastro com lançamento de receita, despesa ou transferência. */
const CADASTRO_ROUTE = /\/(contas-financeiras|cadastros|configuracoes|seguranca)(\/|$)|\/empresa\/(centros|parceiros|mapa)(\/|$)|\/patrimonio\/bens(\/|$)/;

/** "+ Novo": Receita, Despesa (E3) ou Transferência (E4). Só aparece habilitado com permissão de escrita na entidade em uso. */
function NewMenu() {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const { current } = useEntities();
  const flow = useTxFlow();
  const can = current?.permission === "total";
  const go = (k: "receita" | "despesa" | "transferencia") => { setOpen(false); if (k === "transferencia") flow.openTransfer(); else flow.openNew(k); };
  return (
    <div className="menu-wrap" ref={ref}>
      <button className="btn-new" disabled={!can} aria-disabled={!can} aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen(!open)} data-new-menu title={can ? "Novo lançamento" : "Acesso somente leitura nesta entidade"}>
        <PlusIcon size={18} /><span className="lbl">Novo</span> <ChevronDown size={14} />
      </button>
      {open && (
        <div className="menu" role="menu">
          <button role="menuitem" onClick={() => go("receita")} data-new="receita">Receita</button>
          <button role="menuitem" onClick={() => go("despesa")} data-new="despesa">Despesa</button>
          <button role="menuitem" onClick={() => go("transferencia")} data-new="transferencia">Transferência</button>
        </div>
      )}
    </div>
  );
}

interface NoteItem { id: string; kind: string; stage: string; dueDate: string; title: string; read: boolean }
const STAGE_LABEL: Record<string, string> = { atrasado: "Em atraso", hoje: "Vence hoje", antecipado: "A vencer" };

/** Sino de avisos de vencimento: contagem de não lidos, lista e atalho para as preferências. Consulta ao abrir a tela e a cada 5 minutos. */
function Notifications({ realm }: { realm: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<{ unread: number; items: NoteItem[] }>({ unread: 0, items: [] });
  const ref = useOutside(open, () => setOpen(false));
  const nav = useNavigate();
  const load = () => { void api<{ unread: number; items: NoteItem[] }>("GET", `/api/${realm}/notifications`).then(setData).catch(() => undefined); };
  useEffect(() => { load(); const t = setInterval(load, 300_000); return () => clearInterval(t); }, [realm]); // eslint-disable-line react-hooks/exhaustive-deps
  const markAll = () => { void api("POST", `/api/${realm}/notifications/read`, { all: true }).then(load).catch(() => undefined); };
  return (
    <div className="menu-wrap" ref={ref}>
      <button className="top-icon" style={{ position: "relative" }} onClick={() => { const o = !open; setOpen(o); if (o) load(); }} aria-label="Notificações" title={data.unread ? `${data.unread} aviso(s) não lido(s)` : "Notificações"} aria-expanded={open} data-bell>
        <BellIcon />{data.unread > 0 && <span className="bell-badge" data-bell-count>{data.unread > 9 ? "9+" : data.unread}</span>}
      </button>
      {open && (
        <div className="menu note-menu" role="dialog" aria-label="Notificações">
          <div className="mh" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Avisos de vencimento</span>
            {data.unread > 0 && <button className="link" style={{ width: "auto", padding: 0 }} onClick={markAll} data-note-readall>Marcar todos como lidos</button>}
          </div>
          {data.items.length === 0
            ? <p className="muted" style={{ margin: "6px 10px 10px", fontSize: 13 }}>Nenhum vencimento próximo. Tudo em dia.</p>
            : <ul className="note-list">{data.items.map((n) => (
                <li key={n.id} className={`note ${n.stage}${n.read ? " read" : ""}`} data-note>
                  <button onClick={() => { setOpen(false); nav(`/${realm}/contas-pagar-receber`); }}>
                    <span className="note-stage">{STAGE_LABEL[n.stage] ?? n.stage}</span>
                    <span>{n.title}</span>
                  </button>
                </li>))}</ul>}
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 6, paddingTop: 6 }}>
            <button onClick={() => { setOpen(false); nav(`/${realm}/configuracoes?aba=avisos`); }} data-note-prefs>Configurar avisos</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileMenu(p: ShellProps) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const { pref, setPref } = useTheme();
  const choose = (t: ThemePref) => {
    setPref(t);
    // preferência salva por usuário, no realm da própria sessão
    void api("PUT", `/api/${p.realm}/preferences/theme`, { theme: t }).catch(() => { /* mantém local; reaplicará na próxima escolha */ });
  };
  return (
    <div className="menu-wrap" ref={ref}>
      <button className="profile" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Perfil">
        {p.realm !== "admin" ? <PjAvatar name={p.displayName} realm={p.realm} /> : <span className="avatar">{initials(p.displayName)}</span>}
        <span className="pname">{p.displayName}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="mh" title={p.email}>{p.email}</div>
          <hr />
          <div className="mh">Tema</div>
          {(["claro", "escuro", "automatico"] as const).map((t) => (
            <button key={t} role="menuitemradio" aria-checked={pref === t} className={pref === t ? "sel" : ""} onClick={() => choose(t)} data-theme-option={t}>
              {pref === t ? "✓ " : " "}{THEME_LABEL[t]}
            </button>
          ))}
          <hr />
          {p.settingsTo && <Link to={p.settingsTo} className="mi" onClick={() => setOpen(false)}>Configurações</Link>}
          <button onClick={p.onLogout}><LogOutIcon size={16} />Sair</button>
        </div>
      )}
    </div>
  );
}

declare const __APP_VERSION__: string;
declare const __BUILD_ID__: string;
